import type { ColDef, ICellRendererParams, SortChangedEvent } from 'ag-grid-community'
import { Download, Plus, Search, Shield } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminDataGrid } from '@/components/grid/AdminDataGrid'
import { PaginationControls } from '@/components/grid/PaginationControls'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatDate, timeAgo } from '@/lib/utils'
import { useUsersStore } from '@/stores/users-store'

const ROLE_TABS = [
  { value: '', label: 'All' },
  { value: 'user', label: 'Users' },
  { value: 'admin', label: 'Admins' },
]

const BANNED_TABS = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Active' },
  { value: 'true', label: 'Banned' },
]

const EMAIL_VERIFICATION_TABS = [
  { value: '', label: 'All Emails' },
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Unverified' },
]

const roleStyles: Record<string, string> = {
  admin: 'bg-accent/10 text-accent border-accent/20',
  user: 'bg-ink-faint/10 text-ink-faint border-ink-faint/20',
}

const providerStyles: Record<string, { label: string; style: string }> = {
  credential: { label: 'Email', style: 'bg-ink-faint/10 text-ink-faint border-ink-faint/20' },
  apple: { label: 'Apple', style: 'bg-ink/10 text-ink border-ink/20' },
}

type SortField = 'name' | 'email' | 'role' | 'availableCredits' | 'lastActiveAt' | 'createdAt'
type UserRow = ReturnType<typeof useUsersStore.getState>['users'][number]
type UserCellParams = ICellRendererParams<UserRow>

const verificationStyles = {
  verified: 'border-positive/20 bg-positive/10 text-positive',
  unverified: 'border-warning/20 bg-warning/10 text-warning',
}

const passiveServerComparator = () => 0

function exportCsv(users: Array<Record<string, unknown>>) {
  const headers = [
    'Name',
    'Email',
    'Provider',
    'Account ID',
    'Role',
    'Email Verified',
    'Credits',
    'Banned',
    'Last Active',
    'Joined',
  ]
  const rows = users.map((u) => [
    u.name ?? '',
    u.email ?? '',
    u.providerId === 'apple' ? 'Apple' : 'Email',
    u.accountId ?? '',
    u.role ?? 'user',
    u.emailVerified ? 'Yes' : 'No',
    u.availableCredits ?? 0,
    u.banned ? 'Yes' : 'No',
    u.lastActiveAt ? formatDate(u.lastActiveAt as string) : '—',
    u.createdAt ? formatDate(u.createdAt as string) : '—',
  ])

  const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${v}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `lookbookiq-users-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function Users() {
  useAuth()
  const navigate = useNavigate()
  const {
    users,
    pagination,
    roleFilter,
    emailVerificationFilter,
    bannedFilter,
    sortField,
    sortDirection,
    isLoading,
    setSearchQuery,
    setRoleFilter,
    setEmailVerificationFilter,
    setBannedFilter,
    setSort,
    fetchUsers,
    createUser,
  } = useUsersStore()

  const [searchInput, setSearchInput] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState({
    email: '',
    name: '',
    firstName: '',
    lastName: '',
    role: 'user' as 'user' | 'admin',
    emailVerified: true,
  })
  const [createError, setCreateError] = useState('')
  const [createSuccess, setCreateSuccess] = useState<{
    email: string
    password: string
  } | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const userColumns: Array<ColDef<UserRow>> = [
    {
      colId: 'name',
      headerName: 'Name',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      comparator: passiveServerComparator,
      sort: sortField === 'name' ? sortDirection : undefined,
      flex: 1.3,
      minWidth: 230,
      cellRenderer: (params: UserCellParams) => {
        const data = params.data
        if (!data) return null
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/10 font-mono text-[10px] font-medium text-accent">
              {(data.name?.[0] ?? data.email[0]).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate font-mono text-xs text-ink">{data.name}</p>
              {data.banned ? (
                <span className="font-mono text-[9px] text-negative">banned</span>
              ) : null}
            </div>
          </div>
        )
      },
    },
    {
      field: 'email',
      colId: 'email',
      headerName: 'Email',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      comparator: passiveServerComparator,
      sort: sortField === 'email' ? sortDirection : undefined,
      flex: 1.4,
      minWidth: 240,
      cellRenderer: (params: UserCellParams) =>
        params.data ? (
          <span className="font-mono text-xs text-ink-muted">{params.data.email}</span>
        ) : null,
    },
    {
      colId: 'provider',
      headerName: 'Provider',
      minWidth: 130,
      maxWidth: 150,
      cellRenderer: (params: UserCellParams) => {
        const data = params.data
        if (!data) return null
        const provider =
          providerStyles[data.providerId ?? 'credential'] ?? providerStyles.credential
        return (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
              provider.style
            )}
          >
            {provider.label}
          </span>
        )
      },
    },
    {
      field: 'role',
      colId: 'role',
      headerName: 'Role',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      comparator: passiveServerComparator,
      sort: sortField === 'role' ? sortDirection : undefined,
      minWidth: 130,
      maxWidth: 160,
      cellRenderer: (params: UserCellParams) => {
        const data = params.data
        if (!data) return null
        return (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
              roleStyles[data.role ?? 'user'] ?? roleStyles.user
            )}
          >
            {data.role === 'admin' ? <Shield className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> : null}
            {data.role ?? 'user'}
          </span>
        )
      },
    },
    {
      field: 'emailVerified',
      colId: 'emailVerified',
      headerName: 'Email',
      minWidth: 140,
      maxWidth: 160,
      cellRenderer: (params: UserCellParams) =>
        params.data ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
              params.data.emailVerified
                ? verificationStyles.verified
                : verificationStyles.unverified
            )}
          >
            {params.data.emailVerified ? 'verified' : 'unverified'}
          </span>
        ) : null,
    },
    {
      field: 'availableCredits',
      colId: 'availableCredits',
      headerName: 'Credits',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      comparator: passiveServerComparator,
      sort: sortField === 'availableCredits' ? sortDirection : undefined,
      minWidth: 110,
      maxWidth: 130,
      cellRenderer: (params: UserCellParams) =>
        params.data ? (
          <span className="font-mono text-xs tabular-nums text-ink">
            {params.data.availableCredits ?? 0}
          </span>
        ) : null,
    },
    {
      field: 'lastActiveAt',
      colId: 'lastActiveAt',
      headerName: 'Last Active',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      comparator: passiveServerComparator,
      sort: sortField === 'lastActiveAt' ? sortDirection : undefined,
      minWidth: 140,
      maxWidth: 170,
      cellRenderer: (params: UserCellParams) =>
        params.data ? (
          <span
            className="font-mono text-[11px] text-ink-faint"
            title={params.data.lastActiveAt ? formatDate(params.data.lastActiveAt) : undefined}
          >
            {params.data.lastActiveAt ? timeAgo(params.data.lastActiveAt) : '—'}
          </span>
        ) : null,
    },
    {
      field: 'createdAt',
      colId: 'createdAt',
      headerName: 'Joined',
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      comparator: passiveServerComparator,
      sort: sortField === 'createdAt' ? sortDirection : undefined,
      minWidth: 130,
      maxWidth: 160,
      cellRenderer: (params: UserCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] text-ink-faint">
            {params.data.createdAt ? formatDate(params.data.createdAt) : '—'}
          </span>
        ) : null,
    },
  ]

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleSearch = () => setSearchQuery(searchInput)

  const handleSortChanged = (event: SortChangedEvent<UserRow>) => {
    const nextSort = event.api
      .getColumnState()
      .find(
        (column) => column.sort && column.colId !== 'provider' && column.colId !== 'emailVerified'
      )

    if (!nextSort?.sort) return

    const nextField = nextSort.colId as SortField
    if (nextField === sortField && nextSort.sort === sortDirection) return
    setSort(nextField, nextSort.sort)
  }

  const handleCreateUser = async (event: FormEvent) => {
    event.preventDefault()
    setCreateError('')
    setCreateSuccess(null)

    if (!createForm.email.trim()) {
      setCreateError('Email is required')
      return
    }

    if (!createForm.name.trim() && !createForm.firstName.trim() && !createForm.lastName.trim()) {
      setCreateError('Provide a name or at least a first/last name')
      return
    }

    setIsCreating(true)

    try {
      const result = await createUser({
        email: createForm.email.trim(),
        name: createForm.name.trim() || undefined,
        firstName: createForm.firstName.trim() || undefined,
        lastName: createForm.lastName.trim() || undefined,
        role: createForm.role,
        emailVerified: createForm.emailVerified,
      })

      setCreateSuccess({
        email: result.user.email,
        password: result.temporaryPassword,
      })
      setCreateForm({
        email: '',
        name: '',
        firstName: '',
        lastName: '',
        role: 'user',
        emailVerified: true,
      })
      setShowCreateForm(true)
    } catch (error) {
      setCreateError((error as Error).message)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <AppShell title="Users">
      <div className="space-y-5">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-md border border-dashed border-border bg-surface">
            {ROLE_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setRoleFilter(tab.value)}
                className={cn(
                  'px-3 py-2 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                  roleFilter === tab.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex rounded-md border border-dashed border-border bg-surface">
            {BANNED_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setBannedFilter(tab.value)}
                className={cn(
                  'px-3 py-2 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                  bannedFilter === tab.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex rounded-md border border-dashed border-border bg-surface">
            {EMAIL_VERIFICATION_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setEmailVerificationFilter(tab.value)}
                className={cn(
                  'px-3 py-2 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                  emailVerificationFilter === tab.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search name or email..."
                className="w-56 rounded-md border border-dashed border-border bg-surface py-2 pl-8 pr-3 font-mono text-[11px] text-ink placeholder:text-ink-faint"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            >
              Search
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowCreateForm((current) => !current)}
            className="flex items-center gap-1.5 rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
          >
            <Plus className="h-3.5 w-3.5" />
            {showCreateForm ? 'Hide Form' : 'Add User'}
          </button>

          <button
            type="button"
            onClick={() => exportCsv(users as unknown as Array<Record<string, unknown>>)}
            className="ml-auto flex items-center gap-1.5 rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            title="Export CSV"
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </button>
        </div>

        {showCreateForm && (
          <div className="rounded-lg border border-dashed border-border bg-surface p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                  Create User
                </p>
                <p className="mt-1 font-mono text-[11px] text-ink-faint">
                  The console will generate a temporary password and create the credential account
                  through better-auth.
                </p>
              </div>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    Email
                  </span>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, email: event.target.value }))
                    }
                    placeholder="user@example.com"
                    className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    Full Name
                  </span>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, name: event.target.value }))
                    }
                    placeholder="Optional if first/last name is set"
                    className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    First Name
                  </span>
                  <input
                    type="text"
                    value={createForm.firstName}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, firstName: event.target.value }))
                    }
                    className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    Last Name
                  </span>
                  <input
                    type="text"
                    value={createForm.lastName}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, lastName: event.target.value }))
                    }
                    className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-faint"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    Role
                  </span>
                  <select
                    value={createForm.role}
                    onChange={(event) =>
                      setCreateForm((current) => ({
                        ...current,
                        role: event.target.value as 'user' | 'admin',
                      }))
                    }
                    className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2 font-mono text-xs text-ink"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>

                <label className="flex items-center gap-2 rounded-md border border-dashed border-border bg-canvas px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={createForm.emailVerified}
                    onChange={(event) =>
                      setCreateForm((current) => ({
                        ...current,
                        emailVerified: event.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-border"
                  />
                  <span className="font-mono text-[11px] text-ink">Mark email as verified</span>
                </label>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={isCreating}
                  className="rounded-md bg-accent px-4 py-2 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false)
                    setCreateError('')
                    setCreateSuccess(null)
                  }}
                  className="rounded-md border border-dashed border-border bg-canvas px-4 py-2 font-mono text-xs text-ink-muted transition-colors hover:bg-surface-alt"
                >
                  Cancel
                </button>
              </div>
            </form>

            {createError && (
              <p className="mt-3 font-mono text-[11px] text-negative">{createError}</p>
            )}

            {createSuccess && (
              <div className="mt-4 rounded-lg border border-dashed border-positive/30 bg-positive/5 p-4">
                <p className="font-mono text-[10px] tracking-wider text-positive uppercase">
                  Temporary Password
                </p>
                <p className="mt-1 font-mono text-xs text-ink">{createSuccess.email}</p>
                <p className="mt-2 break-all rounded-md bg-canvas px-3 py-2 font-mono text-sm text-ink">
                  {createSuccess.password}
                </p>
                <p className="mt-2 font-mono text-[10px] text-ink-faint">
                  This is only shown here after creation.
                </p>
              </div>
            )}
          </div>
        )}

        <AdminDataGrid
          columns={userColumns}
          emptyMessage="No users found"
          getRowClass={() => 'cursor-pointer'}
          getRowId={({ data }) => data.id}
          loading={isLoading}
          onRowClicked={({ data }) => {
            if (data) navigate(`/users/${data.id}`)
          }}
          onSortChanged={handleSortChanged}
          rows={users}
        />

        <PaginationControls
          label="users"
          onNext={() => fetchUsers(pagination.page + 1)}
          onPrevious={() => fetchUsers(pagination.page - 1)}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      </div>
    </AppShell>
  )
}
